<template>
  <div
    v-if="!pageLoading"
    class="requisition-report-header"
  >
    <div class="header-left">
      <h3 class="req-title">
        {{ title }}
      </h3>
      <div class="mt-2 d-flex align-center justify-space-between px-6 w-100">
        <p
          v-if="reqId"
          class="requisition-id mr-2"
        >
          Req ID: {{ reqId }}
        </p>
        <RequisitionLeverMessage v-if="showLeverMessage" />
      </div>
    </div>
    <div class="header-right">
      <div class="header-right-req-info">
        <div class="req-status req-info-item">
          <h4>Status</h4>
          <div v-if="showOnHold" class="w-50">
            <DpplTooltip
            >
              <template #activator="{ onMouseenter, onMouseleave }">
                <div
                  @mouseenter="onMouseenter"
                  @mouseleave="onMouseleave"
                >
                  On-Hold
                </div>
              </template>
              <template #tooltip-content>
                <div>
                  <h5 class="mb-4">
                    On-Hold Requisition
                  </h5>
                  <p>
                    {{ reqOnHoldTooltipText }}
                  </p>
                </div>
              </template>
            </DpplTooltip>
          </div>
          <span v-else>{{ status }}</span>
          <span v-if="status === 'closed' && closedAt">
            | {{ formatClosedDate }}
          </span>
        </div>
        <div
          v-if="status !== 'draft'"
          class="req-days-open req-info-item"
        >
          <h4>
            Days Open
            <InsightsTooltip :tooltip="tooltip" />
          </h4>
          {{ calculatedDaysOpen }}
        </div>
      </div>
      <div class="d-flex flex-column justify-space-between align-center">
        <RequisitionReportCommentsToggle
          v-if="showCommentsToggle"
          :req-id="reqId"
          :req-uuid="reqUuid"
          :req-title="title"
        />
        <InsightsExport :disabled="noDataForExport" />
        <InsightsShare :disabled="noDataForExport" />
      </div>
    </div>
  </div>
  <div
    v-else
    class="header-loader-wrapper"
  >
    <RequisitionReportHeaderSkeleton />
  </div>
</template>

<script>
import { mapState } from 'pinia';
import { format } from 'date-fns';
import { useAppLoading } from '@/stores/app/loading';
import { useCommenting } from '@/stores/commenting/commenting';
import { useRequisitionReport } from '@/stores/requisitions/requisitionReport';
import { useTooltips } from '@/stores/insights/tooltips';
import RequisitionReportHeaderSkeleton from '@/components/requisitions/skeletonLoaders/RequisitionReportHeaderSkeleton.vue';
import InsightsTooltip from '@/components/insights/InsightsTooltip.vue';
import { reqOnHoldTooltipText } from '@/helpers/tooltips';
import DpplTooltip from '@/components/datapeople/DpplTooltip.vue';
import { TYPE_LEVER, TYPE_WORKDAY } from '@/helpers/constants/integrationTypes';
import { useAppAuthData } from '@/stores/app/authData';
import RequisitionLeverMessage from '@/components/requisitions/RequisitionLeverMessage.vue';
import InsightsExport from '@/components/insights/ExportModal/InsightsExport.vue';
import InsightsShare from '@/components/insights/ShareModal/InsightsShare.vue';
import { getReqDaysOpen, getTimezoneOffsetDate } from './RequisitionReportApplicantsWidget/helpers';
import RequisitionReportCommentsToggle from './RequisitionReportCommentsToggle.vue';

export default {
  name: 'RequisitionReportHeader',
  components: {
    RequisitionReportCommentsToggle,
    InsightsTooltip,
    RequisitionReportHeaderSkeleton,
    DpplTooltip,
    RequisitionLeverMessage,
    InsightsExport,
    InsightsShare,
  },
  data() {
    return {
      tooltipKeys: {
        open: 'req-report-open-days-open',
        closed: 'req-report-closed-days-open',
      },
      reqOnHoldTooltipText,
    };
  },
  computed: {
    ...mapState(useRequisitionReport, [
      'title',
      'reqId',
      'reqUuid',
      'status',
      'openedAt',
      'closedAt',
      'onHold',
      'noDataForExport',
    ]),
    ...mapState(useAppLoading, ['pageLoading']),
    ...mapState(useCommenting, ['showCommentsToggle']),
    ...mapState(useTooltips, ['tooltips']),
    ...mapState(useAppAuthData, ['authData']),
    showLeverMessage() {
      return this.integrationTypeKey === TYPE_LEVER;
    },
    isWorkday() {
      return this.integrationTypeKey === TYPE_WORKDAY;
    },
    showOnHold() {
      return (this.status === 'open' || (this.status === 'draft' && this.isWorkday)) && this.onHold;
    },
    integrationTypeKey() {
      return this.authData.integrationTypeKey;
    },
    calculatedDaysOpen() {
      return getReqDaysOpen({
        openedAt: this.openedAt,
        closedAt: this.closedAt,
        status: this.status,
      });
    },
    formatClosedDate() {
      if (!this.closedAt) return 'N/A';

      const localClosedDate = getTimezoneOffsetDate(this.closedAt);

      return format(new Date(localClosedDate), 'MM/dd/yy');
    },
    tooltip() {
      const tooltipKey = this.tooltipKeys[this.status];
      const { body_r: html, mode } = this.tooltips?.[tooltipKey] || {};

      return {
        html,
        mode,
        key: tooltipKey,
      };
    },
  },
};

</script>

<style lang="less" scoped>
@import '@/less/mixins';

.requisition-report-header {
  width: 100%;
  max-height: 124px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.header-left {
  grid-column: span 2;
  height: 100%;
  background: @white;
  display: grid;
  align-content: center;
  justify-items: start;
  border-radius: 8px;
  border: 1px solid @stroke-default;
}

.header-right {
  display: grid;
  grid-template-columns: 1fr 40px;
  gap: 8px;

  .header-right-req-info {
    display: grid;
    grid-template-columns: 50% 50%;
    width: 100%;
    background-color: @white;
    padding: 24px 16px;
    border-radius: 8px;
    border: 1px solid @stroke-default;

    .req-info-item {
      font-size: 16px;
      font-weight: 700;
      line-height: 20px;
      color: @text-brand;
      text-align: left;
      text-transform: capitalize;

      &.req-status {
        grid-column: 1;
      }

      &.req-days-open {
        grid-column: 2;
        border-left: 1px solid @stroke-default;
        padding-left: 16px;
      }
    }

    h4 {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      font-size: 16px;
      font-weight: 300;
      line-height: 20px;
      margin-bottom: 4px;
      color: @text-darkest;

      :deep(.dppl-info-tooltip) {
        margin-left: 8px;
      }
    }
  }
}

.header-loader-wrapper {
  display: flex;
  width: 100%;
  height: 124px;
}

.req-title {
  font-size: 26px;
  line-height: 28px;
  letter-spacing: 0.5px;
  font-weight: 700;
  padding-left: 24px;
  text-align: left;
}

.requisition-id {
  font-size: 13px !important;
  line-height: 16px !important;
  letter-spacing: 0.431px !important;
}

:deep(.download-chip-activator), :deep(.share-chip-activator) {
  margin-left: 0px !important;
}
</style>
